import { observer } from "mobx-react-lite";
import { useNavigate } from "react-router-dom";
import { appState } from "@/app/appState";
import { chatState } from "@/app/chatState";
import { RunEntry, runsState } from "@/app/runsState";
import { shortenLabel } from "@/app/workspace/chat/chatObjectEditorWindow/chatObjectEditorWindow";
import { ToolRunStatus, ToolStatusIcon } from "@/app/workspace/chat/chatWindow/chatMessage";

export const chatPathForTags = (tagIds: number[]) =>
    tagIds.length ? `/workspace/chat-${[...tagIds].sort((a, b) => a - b).join("-")}` : "/workspace/chat";

const scopeLabel = (tagIds: number[]) => {
    const names = tagIds.map((id) => appState.tags.find((t) => t.id === id)?.name).filter((n): n is string => !!n);
    return names.length ? names.map((n) => shortenLabel(n, 12)).join(", ") : appState.t("nav.chat");
};

/** A run as a tool-result status: streaming while running, then success or error until seen. */
export const runIndicator = (run: RunEntry | undefined): ToolRunStatus | undefined => {
    if (!run) return undefined;
    if (run.status === "running") return "streaming";
    if (!run.finishedUnseen) return undefined;
    return run.status === "failed" ? "error" : "success";
};

const INDICATOR_TITLE: Record<ToolRunStatus, string> = {
    streaming: "chat.runRunning",
    success: "chat.runFinished",
    error: "chat.runFailed",
};

/** Same dot as tool results in the chat: pulsing while the run goes, green or red once it finished unseen. */
export const RunBadge = observer(({ run }: { run: RunEntry | undefined }) => {
    const status = runIndicator(run);
    if (!status) return null;
    return (
        <span className="inline-flex" title={appState.t(INDICATOR_TITLE[status])}>
            <ToolStatusIcon status={status} />
        </span>
    );
});

/** Chats running in the background, or finished since the user last looked; click to open. */
export const RunningChats = observer(() => {
    const navigate = useNavigate();
    const runs = runsState.notable.filter((r) => !(chatState.mounted && r.conversation_id === chatState.conversationId));
    if (runs.length === 0) return null;
    return (
        <div className="nav-group">
            {runs.map((run) => (
                <button
                    key={run.conversation_id}
                    type="button"
                    className="btn btn-ghost run-chip"
                    title={scopeLabel(run.tag_ids)}
                    onClick={() => navigate(chatPathForTags(run.tag_ids))}
                >
                    <RunBadge run={run} />
                    <small className="run-chip-label">{scopeLabel(run.tag_ids)}</small>
                </button>
            ))}
        </div>
    );
});
